import React from "react"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { WordHighlight } from "@/components/transcript/word-highlight"
import type { SubvalueByTimestamp } from "@/types/api/MetricOutput"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"
import type { Message } from "@/types/Transcript"

import { BinaryMetricRecord } from "../base/BinaryMetricRecord"

const SIGNAL_REGION_COLOR = "var(--coval-danger-surface)"

interface MetricResultStats {
  name?: string
  values?: Record<string, unknown>
}

function getSubvalues(
  mo: Pick<RunMetricOutput, "subvalues_by_timestamp">,
): SubvalueByTimestamp[] {
  return mo.subvalues_by_timestamp ?? []
}

function formatTime(seconds: number): string {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(seconds, 0) : 0
  const minutes = Math.floor(safeSeconds / 60)
  const remainingSeconds = Math.floor(safeSeconds % 60)
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`
}

function formatCategory(category: string | null | undefined): string {
  return category ? category.replace(/_/g, " ") : "match"
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function getMatchedEventCount(mo: RunMetricOutput): number {
  return readNumber(mo.result?.count) ?? getSubvalues(mo).length
}

function getMatchRate(mo: RunMetricOutput): number | null {
  const frequency = readNumber(mo.result?.frequency)
  if (frequency != null) {
    return frequency
  }
  return null
}

function getCategoryCounts(mo: RunMetricOutput): Record<string, number> {
  const stats = Array.isArray(mo.result?.stats)
    ? (mo.result.stats as MetricResultStats[])
    : []
  const categoryStats = stats.find(
    (statsEntry) => statsEntry.name === "matched_category_counts",
  )
  const values = categoryStats?.values ?? {}
  return Object.fromEntries(
    Object.entries(values)
      .map(([key, value]) => [key, readNumber(value)] as const)
      .filter((entry): entry is readonly [string, number] => entry[1] != null),
  )
}

function getPatternName(subvalue: SubvalueByTimestamp): string | null {
  const patternName = subvalue.metadata?.pattern_name
  return typeof patternName === "string" ? patternName : null
}

export class TranscriptSignalMetricRecord extends BinaryMetricRecord {
  invertYesNoColors = true
  supportsBinaryAggregateOverview = true as const

  getHighlightedMessages(mo: RunMetricOutput): number[] {
    return [
      ...new Set(
        getSubvalues(mo)
          .filter((subvalue) => typeof subvalue.message_index === "number")
          .map((subvalue) => subvalue.message_index as number),
      ),
    ]
  }

  getHighlightedWords(
    mo: RunMetricOutput,
    transcript: Message[],
  ): WordHighlight[] | null {
    const highlights = getSubvalues(mo)
      .map((subvalue): WordHighlight | null => {
        if (
          typeof subvalue.message_index !== "number" ||
          typeof subvalue.char_start !== "number" ||
          typeof subvalue.char_end !== "number"
        ) {
          return null
        }

        const message = transcript[subvalue.message_index]
        const content =
          typeof message?.content === "string" ? message.content : ""
        if (!content) {
          return null
        }

        const startOffset = Math.max(
          0,
          Math.min(subvalue.char_start, content.length),
        )
        const endOffset = Math.max(
          startOffset,
          Math.min(subvalue.char_end, content.length),
        )
        if (endOffset <= startOffset) {
          return null
        }

        return {
          messageIndex: subvalue.message_index,
          startOffset,
          endOffset,
        }
      })
      .filter((highlight): highlight is WordHighlight => highlight != null)

    return highlights.length > 0 ? highlights : null
  }

  getAudioRegions(
    mo: MetricOutputDisplaySource | undefined,
  ): AudioRegionParams[] {
    if (!mo) {
      return []
    }

    return (mo.subvalues_by_timestamp ?? []).map((subvalue, index) => {
      const start = subvalue.start_offset
      const end =
        subvalue.end_offset > subvalue.start_offset
          ? subvalue.end_offset
          : subvalue.start_offset + 0.5
      return {
        id: `transcript-signal:${this.id || this.metric_name}:${index}`,
        start,
        end,
        color: SIGNAL_REGION_COLOR,
        drag: false,
        resize: false,
      }
    })
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const subvalues = getSubvalues(mo)
    const categoryCounts = getCategoryCounts(mo)
    const matchedEventCount = getMatchedEventCount(mo)
    const matchRate = getMatchRate(mo)

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-divider bg-content1 rounded-lg p-3">
            <div className="text-xs text-coval-text-muted font-medium mb-1">
              Detected Events
            </div>
            <div className="text-sm font-bold text-foreground">
              {matchedEventCount}
            </div>
          </div>
          <div className="border border-divider bg-content1 rounded-lg p-3">
            <div className="text-xs text-coval-text-muted font-medium mb-1">
              Match Rate
            </div>
            <div className="text-sm font-bold text-foreground">
              {matchRate == null ? "-" : `${(matchRate * 100).toFixed(0)}%`}
            </div>
          </div>
        </div>

        {Object.keys(categoryCounts).length > 0 && (
          <div>
            <div className="text-sm font-semibold mb-2 text-foreground">
              Categories
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(categoryCounts).map(([category, count]) => (
                <span
                  key={category}
                  className="px-2 py-1 rounded border border-divider bg-background text-xs text-foreground"
                >
                  {formatCategory(category)}: {count}
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="text-sm font-semibold mb-2 text-foreground">
            Timeline Matches
          </div>
          {subvalues.length === 0 ? (
            <div className="text-sm text-coval-text-muted">
              No matches detected
            </div>
          ) : (
            <div className="space-y-2">
              {subvalues.map((subvalue, index) => {
                const patternName = getPatternName(subvalue)
                return (
                  <div
                    key={`${subvalue.message_index ?? "event"}-${index}`}
                    className="border border-divider rounded-lg bg-background p-3"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2 py-1 rounded border border-divider bg-content1 text-xs font-semibold text-foreground">
                        {formatCategory(subvalue.string_value)}
                      </span>
                      <span className="text-xs text-coval-text-muted font-mono">
                        {formatTime(subvalue.start_offset)} -{" "}
                        {formatTime(subvalue.end_offset)}
                      </span>
                      <span className="text-xs text-coval-text-muted">
                        message {subvalue.message_index ?? "-"}
                      </span>
                      <span className="text-xs text-coval-text-muted">
                        matches {subvalue.float_value ?? 0}
                      </span>
                    </div>
                    {patternName && (
                      <div className="text-xs text-coval-text-muted mt-2">
                        pattern {patternName}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }
}
